import { gql, useQuery } from "urql";
import type { TypedDocumentNode } from "urql";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Kpi } from "@acto/taskpane/core/domain";
import {
  Kpi as GqlKpi,
  type Granularity as GqlGranularity,
  type KpiComparisonMode as GqlKpiComparisonMode,
} from "@acto/gql/graphql";
import {
  toGqlGranularity,
  toGqlKpi,
  toGqlKpiComparisonMode,
} from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { buildKpiTrend, type KpiSummaryValueData, type KpiTrend } from "./buildKpiTrend";
import { type CorporateGroupEnvelope, firstCorporateGroup } from "./corporateGroupEnvelope";
import { selectGranularity } from "./selectGranularity";

interface KpisData {
  revenue: number | null;
  profit: number | null;
  margin: number | null;
  count: number | null;
  incomingOrder: number | null;
}

interface GroupTimeSeriesData {
  dataPoints: {
    periodStart: string;
    previousPeriodStart: string | null;
    current: KpisData;
    previous: KpisData | null;
  }[];
  totals: KpisData;
  previousTotals: KpisData;
  analysisPeriod: {
    current: { startDate: string; endDate: string };
    previous: { startDate: string; endDate: string };
  };
}

type GroupKpiTrendQueryData = CorporateGroupEnvelope<{ id: string; kpisByTimeSeries: GroupTimeSeriesData | null }>;

interface GroupKpiTrendQueryVariables {
  clientId: string;
  dateRange: { startDate: string; endDate: string };
  granularity: GqlGranularity;
  productGroupId?: string;
  comparisonMode: GqlKpiComparisonMode;
}

const GroupKpiTrendQuery: TypedDocumentNode<GroupKpiTrendQueryData, GroupKpiTrendQueryVariables> = gql`
  query GetCorporateGroupKpiTrend(
    $clientId: ID!
    $dateRange: DateRangeInput!
    $granularity: Granularity!
    $productGroupId: ID
    $comparisonMode: KpiComparisonMode!
  ) {
    clients(first: 1, filter: { id: $clientId }) {
      edges {
        node {
          id
          corporateGroup {
            id
            kpisByTimeSeries(
              dateRange: $dateRange
              granularity: $granularity
              productGroupId: $productGroupId
              comparisonMode: $comparisonMode
            ) {
              dataPoints {
                periodStart
                previousPeriodStart
                current {
                  revenue
                  profit
                  margin
                  count
                  incomingOrder
                }
                previous {
                  revenue
                  profit
                  margin
                  count
                  incomingOrder
                }
              }
              totals {
                revenue
                profit
                margin
                count
                incomingOrder
              }
              previousTotals {
                revenue
                profit
                margin
                count
                incomingOrder
              }
              analysisPeriod {
                current {
                  startDate
                  endDate
                }
                previous {
                  startDate
                  endDate
                }
              }
            }
          }
        }
      }
    }
  }
`;

const KPI_FIELD: Record<GqlKpi, keyof KpisData> = {
  [GqlKpi.Revenue]: "revenue",
  [GqlKpi.Profit]: "profit",
  [GqlKpi.Margin]: "margin",
  [GqlKpi.Orders]: "count",
  [GqlKpi.IncomingOrder]: "incomingOrder",
};

function toSummaryValues(series: GroupTimeSeriesData, kpis: GqlKpi[]): KpiSummaryValueData[] {
  return kpis.map((kpi) => {
    const field = KPI_FIELD[kpi];
    const previousTotal = series.previousTotals[field] ?? 0;
    const totalValue = series.totals[field] ?? 0;

    return {
      kpi,
      totalValue,
      timeSeries: {
        dataPoints: series.dataPoints.map((point) => ({
          periodStart: point.periodStart,
          value: point.current[field] ?? 0,
          previousPeriodStart: point.previousPeriodStart,
        })),
      },
      comparison: {
        totalValue: previousTotal,
        change: {
          percentageChange: previousTotal !== 0 ? ((totalValue - previousTotal) / previousTotal) * 100 : null,
        },
        range: {
          startDate: series.analysisPeriod.previous.startDate,
          endDate: series.analysisPeriod.previous.endDate,
        },
      },
      comparisonTimeSeries: {
        dataPoints: series.dataPoints.map((point) => ({
          periodStart: point.periodStart,
          value: point.previous?.[field] ?? 0,
        })),
      },
    };
  });
}

export interface GroupKpiTrendOptions {
  clientId: string | undefined;
  kpi?: Kpi;
  dateRange: { startDate: string; endDate: string };
  isCustomRange?: boolean;
  productGroupId?: string;
}

/** The corporate group's KPI trend (all members), keyed off one of its member clients. */
export function useGroupKpiTrend({
  clientId,
  kpi = Kpi.REVENUE,
  dateRange,
  isCustomRange = false,
  productGroupId,
}: GroupKpiTrendOptions): KpiTrend {
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const comparisonMode = useComparisonModeContext();
  const tKpi = useTranslations("kpi");

  // Marge is profit-backed: fetch profit alongside the ratio.
  const isMargin = kpi === Kpi.MARGIN;

  const granularity = selectGranularity(dateRange);

  const [result] = useQuery({
    query: GroupKpiTrendQuery,
    variables: {
      clientId: clientId ?? "",
      dateRange,
      granularity: toGqlGranularity(granularity),
      productGroupId: productGroupId ?? undefined,
      comparisonMode: toGqlKpiComparisonMode(comparisonMode),
    },
    requestPolicy: "cache-first",
    pause: !clientId,
  });

  const series = firstCorporateGroup(result.data)?.kpisByTimeSeries;
  const kpisToBuild = isMargin ? [GqlKpi.Profit, GqlKpi.Margin] : [toGqlKpi(kpi)];

  return buildKpiTrend({
    values: series ? toSummaryValues(series, kpisToBuild) : [],
    fetching: result.fetching,
    stale: result.stale,
    selectedKpi: kpi,
    comparisonMode,
    dateRange,
    isCustomRange,
    granularity,
    locale,
    currency,
    tKpi,
  });
}
